import { z } from "zod";

/**
 * Permission catalog. Format: `${object}:${action}`.
 * Every server-side check references one of these constants.
 */
export const PERMISSIONS = [
  // org administration
  "org:manage",
  "org:billing",
  "user:read",
  "user:manage",
  "role:manage",
  "audit:read",
  "theme:manage",
  "app:install",
  "app:manage",
  /** CT-RM-20: register record cards (partner apps, via their token) */
  "app_card:register",
  /** CT-RM-20: post partner events to constituent timelines */
  "timeline:write",
  // constituents
  "constituent:read",
  "constituent:create",
  "constituent:update",
  "constituent:delete",
  "constituent:merge",
  "constituent:export",
  // sensitive field groups
  "field_group:wealth:read",
  "field_group:private_notes:read",
  // giving
  "gift:read",
  "gift:create",
  "gift:update",
  "gift:delete",
  "pledge:read",
  "pledge:manage",
  /** planned / deferred gifts (bequests, annuities, trusts) and legacy society */
  "planned_gift:read",
  "planned_gift:manage",
  "recurring:read",
  "recurring:manage",
  "receipt:issue",
  "receipt:reissue",
  "batch:commit",
  "batch:reverse",
  "fund:manage",
  "campaign:manage",
  // fundraising ops
  "portfolio:read",
  "portfolio:manage",
  /** set per-officer fiscal-year goals (managers); reading goals needs only portfolio:read */
  "portfolio:goals",
  "grant:read",
  "grant:manage",
  "event:read",
  "event:manage",
  "auction:read",
  "auction:manage",
  "volunteer:read",
  "volunteer:manage",
  /** CT-RM-36 follow-up: background-check status/results (sensitive) */
  "volunteer:screening",
  "membership:read",
  "membership:manage",
  // engagement
  "segment:read",
  "segment:manage",
  "sms:send",
  "sms:manage",
  "mail:send",
  "mail:manage",
  "automation:read",
  "automation:manage",
  "email:send_bulk",
  "email:send_personal",
  "email:manage_templates",
  "page:read",
  "page:manage",
  // data
  "import:run",
  "report:read",
  "report:export",
  "report:schedule",
  "document:read",
  "document:manage",
  "custom_field:manage",
  "custom_object:manage",
  // ai
  "ai:use",
] as const;

export type Permission = (typeof PERMISSIONS)[number];
export const zPermission = z.enum(PERMISSIONS);

/**
 * Plain-language description of each permission, phrased as what a holder can
 * do. Shown on the OAuth consent screen (where permissions are the scopes) and
 * anywhere a scope needs explaining to a non-developer.
 */
export const PERMISSION_DESCRIPTIONS: Record<Permission, string> = {
  "org:manage": "Change your organization's settings",
  "org:billing": "Manage billing and your subscription",
  "user:read": "See the people on your team",
  "user:manage": "Invite, suspend and change roles for team members",
  "role:manage": "Create and edit roles and what they can do",
  "audit:read": "Read the audit log",
  "theme:manage": "Change your branding and theme",
  "app:install": "Install and remove marketplace apps",
  "app:manage": "Manage API tokens, webhooks and OAuth apps",
  "app_card:register": "Add information cards to constituent and gift records",
  "timeline:write": "Add events to constituent timelines",
  "constituent:read": "See constituent records: names, contact details and history",
  "constituent:create": "Add new constituents",
  "constituent:update": "Edit constituent records",
  "constituent:delete": "Delete constituents",
  "constituent:merge": "Merge duplicate constituents",
  "constituent:export": "Export constituent lists",
  "field_group:wealth:read": "See wealth and capacity information",
  "field_group:private_notes:read": "Read private notes",
  "gift:read": "See gifts and donation history",
  "gift:create": "Record new gifts",
  "gift:update": "Edit gifts",
  "gift:delete": "Delete gifts",
  "planned_gift:read": "See planned gifts and legacy society membership",
  "planned_gift:manage": "Record and edit planned gifts and legacy society membership",
  "pledge:read": "See pledges",
  "pledge:manage": "Create and change pledges",
  "recurring:read": "See recurring gifts",
  "recurring:manage": "Create and change recurring gifts",
  "receipt:issue": "Issue donation receipts",
  "receipt:reissue": "Reissue or void receipts",
  "batch:commit": "Commit gift batches",
  "batch:reverse": "Reverse committed gift batches",
  "fund:manage": "Manage funds",
  "campaign:manage": "Manage campaigns, appeals and acknowledgment rules",
  "portfolio:read": "See fundraiser portfolios and goals",
  "portfolio:manage": "Assign constituents to fundraisers",
  "portfolio:goals": "Set fundraiser goals",
  "grant:read": "See grants",
  "grant:manage": "Create and change grants",
  "event:read": "See events and registrations",
  "event:manage": "Create and change events",
  "auction:read": "See auctions and bids",
  "auction:manage": "Run auctions",
  "volunteer:read": "See volunteer opportunities and sign-ups",
  "volunteer:manage": "Manage volunteer opportunities and shifts",
  "volunteer:screening": "View and manage volunteer background checks",
  "membership:read": "See memberships",
  "membership:manage": "Create and change memberships",
  "segment:read": "See saved segments",
  "segment:manage": "Create and change segments",
  "sms:send": "Send text messages to constituents",
  "sms:manage": "Manage texting settings",
  "mail:send": "Send printed mail",
  "mail:manage": "Manage letter templates and mail runs",
  "automation:read": "See automations",
  "automation:manage": "Create and change automations",
  "email:send_bulk": "Send bulk email",
  "email:send_personal": "Send email from your connected mailbox",
  "email:manage_templates": "Manage email templates",
  "page:read": "See donation and landing pages",
  "page:manage": "Create and change donation and landing pages",
  "import:run": "Import data",
  "report:read": "Run reports",
  "report:export": "Export reports",
  "report:schedule": "Schedule report deliveries",
  "document:read": "See documents",
  "document:manage": "Upload and delete documents",
  "custom_field:manage": "Manage custom fields",
  "custom_object:manage": "Manage custom objects",
  "ai:use": "Use AI features",
};

export const SYSTEM_ROLES = {
  owner: {
    name: "Owner",
    description: "Full access, including billing and org deletion.",
    permissions: [...PERMISSIONS] as Permission[],
  },
  admin: {
    name: "Admin",
    description: "Full access except billing.",
    permissions: PERMISSIONS.filter((p) => p !== "org:billing") as Permission[],
  },
  manager: {
    name: "Manager",
    description: "Manage fundraising work and users; no org settings.",
    permissions: PERMISSIONS.filter(
      (p) =>
        !["org:manage", "org:billing", "role:manage", "app:install", "app:manage", "app_card:register", "theme:manage"].includes(p),
    ) as Permission[],
  },
  fundraiser: {
    name: "Fundraiser",
    description: "Work constituents, gifts, portfolios, and outreach.",
    permissions: [
      "constituent:read",
      "constituent:create",
      "constituent:update",
      "gift:read",
      "gift:create",
      "pledge:read",
      "planned_gift:read",
      "recurring:read",
      "portfolio:read",
      "portfolio:manage",
      "grant:read",
      "event:read",
      "auction:read",
      "volunteer:read",
      "membership:read",
      "segment:read",
      "sms:send",
      "mail:send",
      "email:send_personal",
      "page:read",
      "report:read",
      "document:read",
      "ai:use",
    ] as Permission[],
  },
  gift_entry: {
    name: "Gift Entry",
    description: "Enter and receipt gifts; read constituents.",
    permissions: [
      "constituent:read",
      "constituent:create",
      "gift:read",
      "gift:create",
      "gift:update",
      "pledge:read",
      "receipt:issue",
      "batch:commit",
      "report:read",
    ] as Permission[],
  },
  read_only: {
    name: "Read Only",
    description: "View everything non-sensitive; change nothing.",
    permissions: [
      "constituent:read",
      "gift:read",
      "pledge:read",
      "planned_gift:read",
      "recurring:read",
      "portfolio:read",
      "grant:read",
      "event:read",
      "auction:read",
      "volunteer:read",
      "membership:read",
      "segment:read",
      "report:read",
      "document:read",
    ] as Permission[],
  },
} as const;

export type SystemRoleKey = keyof typeof SYSTEM_ROLES;
export const zSystemRoleKey = z.enum(
  Object.keys(SYSTEM_ROLES) as [SystemRoleKey, ...SystemRoleKey[]],
);
